import { cn } from '@/lib/utils';
import type { ArtifactSupportInfo } from '@shared/vantaArtifactsTypes';

/**
 * Vanta Artifacts — petits utilitaires d'affichage.
 */

export const formatBytes = (bytes: number | null | undefined) => {
    if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let val = bytes;
    let i = 0;
    while (val >= 1024 && i < units.length - 1) {
        val /= 1024;
        i++;
    }
    return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

/** « 3d 4h », « 5h 12m », « 8m » */
export const formatCountdown = (ms: number) => {
    if (ms <= 0) return '0m';
    const d = Math.floor(ms / 86_400_000);
    const h = Math.floor((ms % 86_400_000) / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

export const formatAgo = (ts: number | null | undefined) => {
    if (!ts) return '—';
    const diff = Date.now() - ts;
    if (diff < 60_000) return 'just now';
    return `${formatCountdown(diff)} ago`;
};

export const formatDate = (ts: number | null | undefined) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
};

export const formatDateTime = (ts: number | null | undefined) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-GB', {
        year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};


//MARK: Tags
type TagTone = 'green' | 'amber' | 'red' | 'blue' | 'violet' | 'muted' | 'primary';
const toneClasses: Record<TagTone, string> = {
    green: 'border-success/50 text-success bg-success/10',
    amber: 'border-warning/50 text-warning bg-warning/10',
    red: 'border-destructive/50 text-destructive bg-destructive/10',
    blue: 'border-info/50 text-info bg-info/10',
    violet: 'border-violet-500/50 text-violet-400 bg-violet-500/10',
    primary: 'border-primary/50 text-primary bg-primary/10',
    muted: 'border-border text-muted-foreground bg-muted/40',
};

export function Tag({ tone, children, className, title }: {
    tone: TagTone;
    children: React.ReactNode;
    className?: string;
    title?: string;
}) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex items-center rounded-sm border px-1.5 py-px text-2xs font-semibold tracking-wider uppercase leading-4 whitespace-nowrap',
                toneClasses[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

export function ReleaseTags({ support }: { support: ArtifactSupportInfo }) {
    return <>
        {support.isRecommended && <Tag tone="green">Recommended</Tag>}
        {support.isLatest && <Tag tone="blue">Latest</Tag>}
        {support.isCritical && <Tag tone="red" title="Minimum build required by Cfx">Critical</Tag>}
    </>;
}

/**
 * Badge de support + compte à rebours
 */
export function SupportCell({ support, compact }: { support: ArtifactSupportInfo; compact?: boolean }) {
    const now = Date.now();
    let tag: React.ReactNode;
    let sub: string | null = null;
    if (support.status === 'supported') {
        tag = <Tag tone="green">Supported</Tag>;
        if (support.supportEndsAt) sub = `Support ends in ${formatCountdown(support.supportEndsAt - now)}`;
    } else if (support.status === 'eos') {
        tag = <Tag tone="amber">End of support</Tag>;
        sub = support.eolAt ? `EOL in ${formatCountdown(support.eolAt - now)}` : null;
    } else if (support.status === 'eol') {
        tag = <Tag tone="red">EOL</Tag>;
        if (support.eolAt) sub = `Since ${formatDate(support.eolAt)}`;
    } else {
        tag = <Tag tone="muted">Unknown</Tag>;
    }
    return (
        <div className={cn('flex gap-x-2 gap-y-0.5', compact ? 'flex-row flex-wrap items-center' : 'flex-col items-start')}>
            {tag}
            {sub && <span className="text-xs text-muted-foreground whitespace-nowrap">{sub}</span>}
        </div>
    );
}
